// ============================================================================
// GumaHUB Main Layout
// Application shell with navigation sidebar and header
// ============================================================================

import React, { useState, useEffect } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '@gumahub/authentication';
import { useTranslation } from 'react-i18next';
import { SUPPORTED_LANGUAGES, changeLanguage, getCurrentLanguage } from '../i18n';
import CompanySwitcher from '../components/CompanySwitcher';
import NavigationSidebar from '../components/NavigationSidebar';
import { useBranding } from '../contexts/BrandingContext';
import { NotificationBell } from '../components/NotificationBell';

// ============================================================================
// Icons
// ============================================================================

const Icons = {
  Menu: () => (
    <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.75}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M4 6h16M4 12h16M4 18h16" />
    </svg>
  ),
  Bell: () => (
    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.75}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
    </svg>
  ),
  Search: () => (
    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.75}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
    </svg>
  ),
  Globe: () => (
    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.75}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9m-9 9a9 9 0 019-9" />
    </svg>
  ),
  ChevronDown: () => (
    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
    </svg>
  ),
  Command: () => (
    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.75}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M18 3a3 3 0 00-3 3v12a3 3 0 003 3 3 3 0 003-3 3 3 0 00-3-3H6a3 3 0 00-3 3 3 3 0 003 3 3 3 0 003-3V6a3 3 0 00-3-3 3 3 0 00-3 3 3 3 0 003 3h12a3 3 0 003-3 3 3 0 00-3-3z" />
    </svg>
  ),
};

// ============================================================================
// Breadcrumb Helper
// ============================================================================

const getBreadcrumb = (pathname: string, t: (key: string) => string): { label: string; path?: string }[] => {
  const segments = pathname.split('/').filter(Boolean);
  const breadcrumbs: { label: string; path?: string }[] = [];

  if (segments.length === 0) {
    return [{ label: t('nav.dashboard') }];
  }

  // Build breadcrumb based on path segments
  const pathMap: Record<string, string> = {
    enterprise: t('nav.enterprise'),
    orders: t('nav.orders'),
    shipments: t('nav.shipments'),
    employees: t('nav.humanResources'),
    absence: t('nav.myLeave'),
    travel: t('nav.myTravel'),
    expense: t('nav.myExpenses'),
    bidding: t('nav.bidManagement'),
    reports: t('nav.reports'),
    settings: t('nav.settings'),
  };

  segments.forEach((segment, index) => {
    const label = pathMap[segment] || segment.charAt(0).toUpperCase() + segment.slice(1).replace(/-/g, ' ');
    const path = '/' + segments.slice(0, index + 1).join('/');
    breadcrumbs.push({ label, path: index < segments.length - 1 ? path : undefined });
  });

  return breadcrumbs;
};

// ============================================================================
// Main Layout Component
// ============================================================================

const MainLayout: React.FC = () => {
  const { user, switchCompany, getAvailableCompanies } = useAuth();
  const { branding, loadMyBranding } = useBranding();
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();

  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    try {
      return localStorage.getItem('gumahub_sidebar_collapsed') === 'true';
    } catch {
      return false;
    }
  });
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [languageDropdownOpen, setLanguageDropdownOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);

  const currentLanguage = getCurrentLanguage();
  const availableCompanies = getAvailableCompanies();
  const breadcrumbs = getBreadcrumb(location.pathname, t);

  // Always load fresh branding from API when user/company changes
  useEffect(() => {
    if (user?.clientId) {
      loadMyBranding();
    }
  }, [user?.clientId, loadMyBranding]);

  const handleToggleSidebar = () => {
    const newState = !sidebarCollapsed;
    setSidebarCollapsed(newState);
    localStorage.setItem('gumahub_sidebar_collapsed', String(newState));
  };

  return (
    <div className="app-container min-h-screen flex bg-slate-50 dark:bg-dark-bg">
      {/* Desktop Sidebar */}
      <div className="hidden lg:block">
        <NavigationSidebar
          collapsed={sidebarCollapsed}
          onToggleCollapse={handleToggleSidebar}
        />
      </div>

      {/* Mobile Sidebar */}
      {mobileMenuOpen && (
        <>
          <div
            className="fixed inset-0 z-30 bg-slate-900/50 dark:bg-black/60 backdrop-blur-sm lg:hidden animate-fade-in"
            onClick={() => setMobileMenuOpen(false)}
          />
          <div className="fixed inset-y-0 left-0 z-40 lg:hidden animate-slide-in-left">
            <NavigationSidebar
              collapsed={false}
              onToggleCollapse={() => setMobileMenuOpen(false)}
            />
          </div>
        </>
      )}

      {/* Main Content Area */}
      <div className={`flex-1 flex flex-col transition-all duration-300 ${sidebarCollapsed ? 'lg:ml-[72px]' : 'lg:ml-[280px]'}`}>
        {/* Top Header */}
        <header className="sticky top-0 z-20 h-16 bg-white/80 dark:bg-dark-surface/80 backdrop-blur-xl border-b border-slate-200/50 dark:border-[#3c4043]/50 flex items-center justify-between px-4 lg:px-6">
          {/* Left Section */}
          <div className="flex items-center gap-4">
            {/* Mobile menu button */}
            <button
              onClick={() => setMobileMenuOpen(true)}
              className="p-2 rounded-xl text-slate-400 dark:text-[#9aa0a6] hover:bg-slate-100 dark:hover:bg-[#3c3c3c] hover:text-slate-600 dark:hover:text-[#e8eaed] transition-colors lg:hidden"
            >
              <Icons.Menu />
            </button>

            {/* Breadcrumb */}
            <nav className="hidden sm:flex items-center gap-2 text-sm">
              {breadcrumbs.map((crumb, index) => (
                <React.Fragment key={index}>
                  {index > 0 && (
                    <svg className="w-4 h-4 text-slate-300 dark:text-[#5f6368]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  )}
                  {crumb.path ? (
                    <button
                      onClick={() => navigate(crumb.path!)}
                      className="text-slate-400 dark:text-[#9aa0a6] hover:text-slate-600 dark:hover:text-[#e8eaed] transition-colors"
                    >
                      {crumb.label}
                    </button>
                  ) : (
                    <span className="font-medium text-slate-700 dark:text-[#e8eaed]">{crumb.label}</span>
                  )}
                </React.Fragment>
              ))}
            </nav>
          </div>

          {/* Right Section */}
          <div className="flex items-center gap-2">
            {/* Search Button */}
            <button
              onClick={() => setSearchOpen(true)}
              className="flex items-center gap-2 px-3 py-1.5 rounded-lg text-slate-400 dark:text-[#9aa0a6] bg-slate-100 dark:bg-[#3c3c3c] hover:bg-slate-200 dark:hover:bg-[#4a4a4a] transition-colors"
            >
              <Icons.Search />
              <span className="hidden sm:inline text-sm">{t('search.placeholder')}</span>
              <kbd className="hidden md:inline-flex items-center gap-0.5 px-1.5 py-0.5 bg-white dark:bg-dark-elevated rounded text-[10px] font-medium text-slate-500 dark:text-[#9aa0a6] border border-slate-200 dark:border-[#5f6368]">
                <Icons.Command />K
              </kbd>
            </button>

            {/* Notifications */}
            <NotificationBell />

            {/* Language Selector */}
            <div className="relative">
              <button
                onClick={() => setLanguageDropdownOpen(!languageDropdownOpen)}
                className="flex items-center gap-1.5 p-2 rounded-xl text-slate-400 dark:text-[#9aa0a6] hover:bg-slate-100 dark:hover:bg-[#3c3c3c] hover:text-slate-600 dark:hover:text-[#e8eaed] transition-colors"
              >
                <Icons.Globe />
                <span className="hidden sm:inline text-lg leading-none">
                  {SUPPORTED_LANGUAGES.find(l => l.code === currentLanguage)?.flag || '🌐'}
                </span>
                <Icons.ChevronDown />
              </button>

              {/* Language Dropdown */}
              {languageDropdownOpen && (
                <>
                  <div
                    className="fixed inset-0 z-40"
                    onClick={() => setLanguageDropdownOpen(false)}
                  />
                  <div className="absolute right-0 mt-2 w-56 bg-white dark:bg-dark-elevated rounded-xl shadow-xl border border-slate-200 dark:border-[#3c4043] py-2 z-50 animate-fade-in">
                    <div className="px-3 py-2 border-b border-slate-100 dark:border-[#3c4043]">
                      <span className="text-xs font-bold text-slate-400 dark:text-[#9aa0a6] uppercase tracking-wider">
                        {t('common:language')}
                      </span>
                    </div>
                    <div className="max-h-80 overflow-y-auto py-1">
                      {SUPPORTED_LANGUAGES.map((lang) => (
                        <button
                          key={lang.code}
                          onClick={() => {
                            changeLanguage(lang.code);
                            setLanguageDropdownOpen(false);
                          }}
                          className={`w-full flex items-center gap-3 px-3 py-2.5 text-left hover:bg-slate-50 dark:hover:bg-[#3c3c3c] transition-colors ${
                            currentLanguage === lang.code
                              ? 'bg-guma-50 dark:bg-[#8ab4f8]/10 text-guma-600 dark:text-[#8ab4f8]'
                              : 'text-slate-700 dark:text-[#e8eaed]'
                          }`}
                        >
                          <span className="text-xl leading-none">{lang.flag}</span>
                          <span className="flex-1 text-sm font-medium">{lang.nativeName}</span>
                          {currentLanguage === lang.code && (
                            <svg className="w-4 h-4 text-guma-500 dark:text-[#8ab4f8]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                              <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                            </svg>
                          )}
                        </button>
                      ))}
                    </div>
                  </div>
                </>
              )}
            </div>

            {/* Company Switcher */}
            {availableCompanies.length > 1 && user && (
              <CompanySwitcher
                companies={availableCompanies}
                currentCompanyId={user.clientId}
                onSwitch={switchCompany}
              />
            )}

            {/* Branding Logo (only logo, no text) */}
            {branding?.logoUrl && (
              <>
                <div className="w-px h-6 bg-slate-200 dark:bg-[#3c4043] mx-1 hidden sm:block" />
                <div className="hidden sm:flex items-center justify-center h-10 px-3 rounded-xl bg-white dark:bg-[#2d2d2d] border border-slate-200 dark:border-[#3c4043] shadow-sm">
                  <img
                    src={branding.logoUrl}
                    alt={branding.companyDisplayName || 'Company Logo'}
                    className="h-7 w-auto max-w-[120px] object-contain"
                  />
                </div>
              </>
            )}
          </div>
        </header>

        {/* Page Content */}
        <main className="flex-1 p-4 lg:p-6 xl:p-8">
          <div className="animate-fade-in-up">
            <Outlet />
          </div>
        </main>

        {/* Footer */}
        <footer className="py-4 px-6 border-t border-slate-100 dark:border-[#3c4043] bg-white/50 dark:bg-dark-surface/50">
          <div className="flex items-center justify-between text-xs text-slate-400 dark:text-[#9aa0a6]">
            {/* Powered by GumaHUB */}
            <div className="flex items-center gap-4">
              <a
                href="https://gumahub.com"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 hover:text-slate-600 dark:hover:text-[#e8eaed] transition-colors"
              >
                <div className="w-5 h-5 rounded bg-gradient-to-br from-guma-500 to-guma-600 flex items-center justify-center">
                  <img
                    src="https://www.gumahub.com/assets/gumahub-Hx4eram0.png"
                    alt="GumaHUB"
                    className="h-3 brightness-0 invert"
                  />
                </div>
                <span>Powered by <span className="font-semibold">GumaHUB</span></span>
              </a>
              <span className="text-slate-300 dark:text-[#5f6368]">•</span>
              <span>
                A product by{' '}
                <a
                  href="https://gumabs.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="font-semibold hover:text-slate-600 dark:hover:text-[#e8eaed] transition-colors"
                >
                  Guma Business Solutions
                </a>
              </span>
            </div>
            {/* System Status */}
            <div className="flex items-center gap-4">
              <span className="flex items-center gap-1.5">
                <span className="w-1.5 h-1.5 rounded-full bg-mint-500 animate-pulse" />
                {t('common:footer.systemStatus')}
              </span>
            </div>
          </div>
        </footer>
      </div>

      {/* Search Modal (placeholder) */}
      {searchOpen && (
        <>
          <div
            className="fixed inset-0 z-50 bg-slate-900/50 dark:bg-black/70 backdrop-blur-sm animate-fade-in"
            onClick={() => setSearchOpen(false)}
          />
          <div className="fixed inset-x-4 top-20 md:inset-x-auto md:left-1/2 md:-translate-x-1/2 md:w-full md:max-w-2xl z-50 animate-fade-in-down">
            <div className="bg-white dark:bg-dark-elevated rounded-2xl shadow-2xl border border-slate-200 dark:border-[#3c4043] overflow-hidden">
              <div className="flex items-center gap-3 px-4 py-3 border-b border-slate-100 dark:border-[#3c4043]">
                <Icons.Search />
                <input
                  type="text"
                  placeholder={t('common:search.placeholder', 'Search anything...')}
                  className="flex-1 text-lg outline-none placeholder:text-slate-400 dark:placeholder:text-[#9aa0a6] bg-transparent text-slate-900 dark:text-[#e8eaed]"
                  autoFocus
                />
                <kbd className="px-2 py-1 bg-slate-100 dark:bg-[#3c3c3c] rounded text-xs font-medium text-slate-500 dark:text-[#9aa0a6]">{t('keyboard.esc')}</kbd>
              </div>
              <div className="p-4 text-center text-slate-400 dark:text-[#9aa0a6] text-sm">
                {t('common:search.hint', 'Start typing to search...')}
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default MainLayout;
